import { useState } from 'react';
import EditAppointmentModal from './EditAppointmentModal';
import DeleteAppointmentModal from './DeleteAppointmentModal';
import ChangeDoctorModal from './ChangeDoctorModal';
import { appointmentTypeLabel } from '../../utils/appointmentTypes';

const STATUS_LABELS = {
  scheduled:   { label: 'Programada',   color: 'badge-blue'   },
  confirmed:   { label: 'Confirmada',   color: 'badge-teal'   },
  'in-progress': { label: 'En curso',   color: 'badge-yellow' },
  completed:   { label: 'Completada',   color: 'badge-green'  },
  cancelled:   { label: 'Cancelada',    color: 'badge-red'    },
  'no-show':   { label: 'No asistió',   color: 'badge-gray'   },
};

// Solo una cita que no se ha atendido se puede borrar; el resto se cancela.
const DELETABLE_STATUSES = ['scheduled', 'confirmed'];

const fmtDate = (d) => new Date(d).toLocaleString('es-CO', {
  timeZone: 'America/Bogota', dateStyle: 'medium', timeStyle: 'short'
});

export default function AppointmentList({ appointments, pagination, onPageChange, onCancel, onEdit, onDelete, doctors, onChangeDoctor }) {
  const [editTarget, setEditTarget]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [doctorTarget, setDoctorTarget] = useState(null);

  if (!appointments.length) {
    return (
      <div className="empty-state">
        <span className="empty-icon">📅</span>
        <p>No hay citas para los filtros seleccionados</p>
      </div>
    );
  }

  return (
    <div className="appointment-list">
      <table className="appt-table">
        <thead>
          <tr>
            <th>Fecha y hora</th>
            <th>Paciente</th>
            <th>Motivo</th>
            <th>Tipo</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {appointments.map(appt => {
            const st = STATUS_LABELS[appt.status] ?? { label: appt.status, color: 'badge-gray' };
            const patient = appt.patient?.user;
            return (
              <tr key={appt._id}>
                <td>{fmtDate(appt.scheduledDate)}</td>
                <td>
                  {patient
                    ? `${patient.profile?.firstName} ${patient.profile?.lastName}`
                    : '—'
                  }
                  <small className="text-muted d-block">{patient?.email}</small>
                </td>
                <td className="reason-cell" title={appt.reason}>{appt.reason}</td>
                <td><span className="type-badge">{appointmentTypeLabel(appt.type)}</span></td>
                <td><span className={`status-badge ${st.color}`}>{st.label}</span></td>
                <td className="actions-cell">
                  <button
                    className="btn-icon btn-edit"
                    title="Editar"
                    onClick={() => setEditTarget(appt)}
                    disabled={appt.status === 'cancelled'}
                  >✏️</button>
                  <button
                    className="btn-icon btn-cancel"
                    title="Cancelar"
                    onClick={() => onCancel(appt._id)}
                    disabled={appt.status === 'cancelled'}
                  >🗑️</button>
                  {/* Sin restricción de estado: también citas pagadas o atendidas. */}
                  {onChangeDoctor && (
                    <button
                      className="btn-icon btn-change-doctor"
                      title="Asignar la cita a otro médico"
                      onClick={() => setDoctorTarget(appt)}
                    >Cambiar médico</button>
                  )}
                  {onDelete && DELETABLE_STATUSES.includes(appt.status) && (
                    <button
                      className="btn-icon btn-delete"
                      title="Eliminar cita definitivamente"
                      onClick={() => setDeleteTarget(appt)}
                    >Eliminar cita</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {pagination?.pages > 1 && (
        <div className="pagination">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              className={`page-btn ${p === pagination.page ? 'active' : ''}`}
              onClick={() => onPageChange(p)}
            >{p}</button>
          ))}
        </div>
      )}

      {editTarget && (
        <EditAppointmentModal
          appointment={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={async (updates) => {
            await onEdit(editTarget._id, updates);
            setEditTarget(null);
          }}
        />
      )}

      {doctorTarget && (
        <ChangeDoctorModal
          appointment={doctorTarget}
          doctors={doctors || []}
          onClose={() => setDoctorTarget(null)}
          onConfirm={async (doctorId, reason) => {
            await onChangeDoctor(doctorTarget._id, doctorId, reason);
            setDoctorTarget(null);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteAppointmentModal
          appointment={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async (reason) => {
            await onDelete(deleteTarget._id, reason);
            setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}
